import { useEffect, useState } from 'react';

import type { ISort, ITableSortingState } from '@/components/common/table/types';
import type { IResourceDatabaseRecord, IResourceViewProps } from '@/api/types';

import { ResourceDatabaseFieldTypeId } from '@/api/enums';
import { getDateFromTime, getFileNameFromFileUrl } from '@/helpers';

interface IUseDynamicTableSortConfig {
  columns: IResourceViewProps[];
  rawData?: IResourceDatabaseRecord[];
  onSortedDataChange?: (sortedData: IResourceDatabaseRecord[]) => void;
  onSortingStateChange?: (sortingState: ITableSortingState<string>) => void;
}

interface IUseDynamicTableSortReturnType {
  sortedData: IResourceDatabaseRecord[];
  onSort: (columnName: string) => void;
  setRawData: (data: IResourceDatabaseRecord[]) => void;
  sortingState: ITableSortingState<string>;
}

const toggleSort = (state?: ISort): ISort | undefined => {
  switch (state) {
    case undefined:
      return 'ASC';
    case 'ASC':
      return 'DESC';
    case 'DESC':
      return 'ASC';
  }
};

const getDynamicTableSortingFunction =
  (columnType: ResourceDatabaseFieldTypeId) =>
  (a: string | null, b: string | null): number => {
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;

    switch (columnType) {
      case ResourceDatabaseFieldTypeId.Text:
      case ResourceDatabaseFieldTypeId.TextMultiline:
      case ResourceDatabaseFieldTypeId.Email:
      case ResourceDatabaseFieldTypeId.Phone:
      case ResourceDatabaseFieldTypeId.WebLink:
      case ResourceDatabaseFieldTypeId.List:
      case ResourceDatabaseFieldTypeId.Calculated:
        return a.localeCompare(b);
      case ResourceDatabaseFieldTypeId.FileAttachment: {
        const displayNameA = getFileNameFromFileUrl(a);
        const displayNameB = getFileNameFromFileUrl(b);
        return displayNameA.localeCompare(displayNameB);
      }
      case ResourceDatabaseFieldTypeId.MemberList: {
        const [, nameA] = a.split('|');
        const [, nameB] = b.split('|');
        return nameA.localeCompare(nameB);
      }
      case ResourceDatabaseFieldTypeId.Number:
      case ResourceDatabaseFieldTypeId.Checkbox:
      case ResourceDatabaseFieldTypeId.Currency:
        return Number(a) - Number(b);
      case ResourceDatabaseFieldTypeId.Date:
      case ResourceDatabaseFieldTypeId.DateTime:
        return new Date(a).getTime() - new Date(b).getTime();
      case ResourceDatabaseFieldTypeId.Time:
        return getDateFromTime(a) - getDateFromTime(b);
    }
  };

export function useDynamicTableSort(params: IUseDynamicTableSortConfig): IUseDynamicTableSortReturnType {
  const config = {
    ...params,
    rawData: params.rawData || [],
  };
  const { onSortedDataChange, onSortingStateChange } = params;
  const [sortingState, setSortingState] = useState<ITableSortingState<string>>({});
  const [rawData, setRawData] = useState<IResourceDatabaseRecord[]>(config.rawData);
  const [sortedData, setSortedData] = useState<IResourceDatabaseRecord[]>(config.rawData);

  const onSort = (colName: string) => {
    setSortingState(sort => {
      const newSortingState = { [colName]: toggleSort(sort[colName]) } as ITableSortingState<string>;
      onSortingStateChange?.(newSortingState);

      return newSortingState;
    });
  };

  useEffect(() => {
    setSortingState({});
  }, [config.columns]);

  useEffect(() => {
    const sort = Object.entries(sortingState).find(([_, state]) => state);
    const rawSorted = [...rawData];

    if (!sort?.[1]) {
      setSortedData(rawSorted);
      onSortedDataChange?.(rawSorted);
      return;
    }

    const [colName, sortState] = sort;

    const columnType = config.columns.find(column => column.field.name === colName);
    if (!columnType) return;
    const sortingFunction = getDynamicTableSortingFunction(columnType.field.rtxtype_Id);

    rawSorted.sort((resA, resB) => {
      const [res1, res2] = sortState === 'DESC' ? [resB, resA] : [resA, resB];
      return sortingFunction(res1[colName] as string | null, res2[colName] as string | null);
    });

    setSortedData(rawSorted);
    onSortedDataChange?.(rawSorted);
  }, [sortingState, rawData, config.columns, onSortedDataChange]);

  return { sortedData, onSort, setRawData, sortingState };
}
